import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faUserAstronaut, faChartLine, faBook, faUser } from '@fortawesome/free-solid-svg-icons';
import { authApi } from '../api/api';
import { clearUserAuth, TOKEN_KEYS } from '../utils/tokenUtils';
import styles from '../styles/MobileBottomNav.module.css';
import { PLAY_STORE_URL } from '../config/constants';

const MobileBottomNav = () => {
    // Only show on mobile devices
    const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 1024);
    
    // Track which submenu is open: null, 'horoscope', or 'account'
    const [openSubmenu, setOpenSubmenu] = React.useState(null);
    
    // Ref for the nav container
    const navRef = React.useRef(null);

    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Check if user is logged in (using proper token key)
    const isLoggedIn = Boolean(localStorage.getItem(TOKEN_KEYS.USER_ACCESS));

    // Logout handler similar to Header.jsx
    const handleLogout = async () => {
        try {
            // Get refresh token before clearing localStorage
            const refreshToken = localStorage.getItem(TOKEN_KEYS.USER_REFRESH);
            
            // Call server-side logout to invalidate refresh token
            if (refreshToken) {
                try {
                    await authApi.post('/auth/logout', { refreshToken });
                } catch (logoutError) {
                    console.warn('⚠️ Server-side logout failed (continuing with client logout):', logoutError.message);
                }
            }
            
            // Clear all user authentication data
            clearUserAuth();
            
            // Close submenu and navigate to home
            setOpenSubmenu(null);
            window.location.href = '/';
            
        } catch (error) {
            console.error('❌ Logout error:', error);
            // Even if server logout fails, clear client-side data
            clearUserAuth();
            setOpenSubmenu(null);
            window.location.href = '/';
        }
    };

    // Helper to toggle submenu
    const handleSubmenuToggle = (menu) => {
        setOpenSubmenu((prev) => (prev === menu ? null : menu));
    }

    // Close submenu when clicking outside
    React.useEffect(() => {
        const handleClick = (e) => {
            // Only close if click is outside nav
            if (navRef.current && !navRef.current.contains(e.target)) {
                setOpenSubmenu(null);
            }
        };
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, []);

    if (!isMobile) return null;

    return (
        <div className={styles.nav} ref={navRef}>
            <a 
                href="/" 
                className={styles.navItem}
                aria-label="Navigate to Home"
            >
                <FontAwesomeIcon icon={faHome} className={styles.icon} />
                <div className={styles.label}>Home</div>
            </a>
            
            <a 
                href="/aboutus" 
                className={styles.navItem}
                aria-label="Navigate to About Us"
            >
                <FontAwesomeIcon icon={faUserAstronaut} className={styles.icon} />
                <div className={styles.label}>About Us</div>
            </a>
            
            <div
                className={`${styles.navItem} ${openSubmenu === 'account' ? styles.navItemActive : ''}`}
                onClick={() => {
                    if (isLoggedIn) {
                        handleSubmenuToggle('account');
                    } else {
                        window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
                    }
                }}
                aria-label={isLoggedIn ? "Account menu" : "Login"}
                aria-expanded={isLoggedIn ? openSubmenu === 'account' : undefined}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (isLoggedIn) {
                            handleSubmenuToggle('account');
                        } else {
                            window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
                        }
                    }
                }}
            >
                <FontAwesomeIcon icon={faUser} className={styles.icon} />
                <div className={styles.label}>{isLoggedIn ? 'Account' : 'Login'}</div>
                {isLoggedIn && openSubmenu === 'account' && (
                    <div 
                        className={styles.submenu}
                        role="menu"
                        aria-label="Account menu"
                    >
                        <a 
                            href="/consultant/profile" 
                            className={styles.submenuItem}
                            role="menuitem"
                            aria-label="Go to My Profile"
                        >
                            My Profile
                        </a>
                        <a 
                            href="/consultant/wallet-recharge" 
                            className={styles.submenuItem}
                            role="menuitem"
                            aria-label="Go to Wallet Recharge"
                        >
                            Wallet Recharge
                        </a>
                        <a 
                            href="/consultant/wallet-passbook" 
                            className={styles.submenuItem}
                            role="menuitem"
                            aria-label="Go to Passbook"
                        >
                            Passbook
                        </a>
                        <a 
                            href="/consultant/order-history" 
                            className={styles.submenuItem}
                            role="menuitem"
                            aria-label="Go to Order History"
                        >
                            Order History
                        </a>
                        <a 
                            href="/consultant/profile-list" 
                            className={styles.submenuItem}
                            role="menuitem"
                            aria-label="Go to Profile List"
                        >
                            Profile List
                        </a>
                        <div 
                            onClick={handleLogout} 
                            className={styles.logoutItem}
                            role="menuitem"
                            aria-label="Logout from account"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleLogout();
                                }
                            }}
                        >
                            Logout
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MobileBottomNav;